import { useNavigate } from "react-router-dom";
import { useLanguage } from '@afsa/shared'
import { motion } from 'framer-motion'
import type { KeyboardEvent } from 'react'

import { fadeUp, staggerContainer } from '@/lib/motion'
import { Heading, Text } from "@/components/ui/Typography";
import { stripHtml } from "@/utils/helpers/html";
import { usePressReleaseHighlights } from '@/lib/api/hooks/press-release'
import type { WebPageSection } from "@/lib/api/types";
import FullPageSpinner from "@/components/ui/FullPageSpinner";

interface PressReleaseHighlightsProps {
  landingPageData?: {
        [key: string]: WebPageSection
  }
}

const PressReleaseHighlights = ({landingPageData}: PressReleaseHighlightsProps) => {

  const navigate = useNavigate();
  const { t } = useLanguage()

  const { data: highlightsData, isFetching } = usePressReleaseHighlights()
  const shouldUseLandingFallback = !highlightsData?.data || highlightsData.data.length === 0


  // TODO: THIS WAS RE-ORDERED AS A TEMPORARY FIX. A NEW UPDATE IS EXPECTED FROM THE API TO PROVIDE THE CORRECT ORDER. THIS IS A TEMPORARY FIX UNTIL THEN.
  const highlightSections = [
    {
      index: 0,
      fallbackKey: 'HOME_OPEN_LETTER',
      fallbackTitleKey: 'open_letter_title',
      fallbackDescKey: 'open_letter_desc',
      fallbackImage:
        'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760303/8f138df83ce1a91fffd0ceb8741da5e48d832864_1_ttaiin.png',
      // type: 'main' as const,
      landingPagePosition: 1, // Assigning slot number for main section
    },
    {
      index: 1,
      fallbackKey: 'HOME_SPACE_SOVEREIGNTY',
      fallbackTitleKey: 'space_sovereignty_title',
      fallbackDescKey: 'space_sovereignty_desc',
      fallbackImage:
        'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760303/313fe16b6541e2c091eadb10a56732f64ea7122b_1_rvi3ka.png',
      // type: 'side' as const,
      landingPagePosition: 2, // Assigning slot number for side section
    },
    {
      index: 2,
      fallbackKey: 'HOME_INNOVATION_TENDER',
      fallbackTitleKey: 'innovation_tender_title',
      fallbackDescKey: 'innovation_tender_desc',
      fallbackImage:
        'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760302/571146ecd45ab83d29e56ef2fda78a5e313df420_1_wezq8s.png',
      // type: 'side' as const,
      landingPagePosition: 3, // Assigning slot number for side section
    },
  ]

  const mainSection = highlightSections.find(section => section.landingPagePosition === 1)
  const sideSections = highlightSections.filter(section => section.landingPagePosition > 1)

  const getSectionContent = (section: (typeof highlightSections)[number]) => {
    // const item = highlightsData?.data?.[section.index]
    const item = highlightsData?.data?.find(pr => pr.landingPagePosition === section.landingPagePosition)
    const fallback = shouldUseLandingFallback ? landingPageData?.[section.fallbackKey] : undefined

    const title = shouldUseLandingFallback
      ? fallback?.title || t(section.fallbackTitleKey)
      : item?.title || t(section.fallbackTitleKey)

    const normalizedDescription = shouldUseLandingFallback
      ? fallback?.description || t(section.fallbackDescKey) || ''
      : item?.content || t(section.fallbackDescKey) || ''

    const image = shouldUseLandingFallback
      ? fallback?.fileUrl || section.fallbackImage
      : item?.coverImage || section.fallbackImage

    return {
      title,
      description: stripHtml(normalizedDescription),
      image,
      sectionTitle: shouldUseLandingFallback ? fallback?.sectionTitle : undefined,
      tags: item?.articleTag,
    }
  }

    // Navigation handlers with proper fallback
  const handleArticleClick = (index: number) => {
    const pressReleaseId = highlightsData?.data?.find(pr => pr.landingPagePosition === index)?.pressReleaseId;
    if (pressReleaseId) {
      navigate(`/media/press-release/${pressReleaseId}`);
    } else {
      // Fallback: navigate to press releases listing
      navigate('/media/press-release');
    }
  };

    const handleArticleKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleArticleClick(index);
    }
  };

  if (isFetching) {
    return <FullPageSpinner />;
  }

  return (
         <motion.section className="" variants={fadeUp}>
        <div className=" max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-8" variants={staggerContainer}>

            {/* Left column - Main Featured Article */}
            {mainSection && (() => {
              const main = getSectionContent(mainSection)

              return (
                <motion.article
                  className="group cursor-pointer flex flex-col gap-4 h-full"
                  onClick={() => handleArticleClick(mainSection.index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => handleArticleKeyDown(e, mainSection.index)}
                  aria-label={`Navigate to ${main.title}`}
                  variants={fadeUp}
                >
                  <div className="overflow-hidden ">
                    <img
                      src={main.image}
                      alt={main.title}
                      className="w-full aspect-video object-cover shadow-lg bg-black md:group-hover:scale-95 transition-transform duration-300"
                    />
                    {main.tags && main.tags.length > 0 && (
                      <div className="flex flex-wrap gap-4 mt-4">
                        {main.tags.map((tag) => (
                          <Text key={tag} as="span" variant="caption" className="bg-wine-100 text-white px-2 py-1 rounded">
                            {tag}
                          </Text>
                        ))}
                      </div>
                    )}
                  </div>

                  <Heading level={3} className=" text-gray-100 mt-2 md:group-hover:underline md:group-hover:decoration-wine-100 group-hover:underline-offset-4 transition-all duration-300">
                    {main.title}
                  </Heading>
                  <Text className="line-clamp-3">
                    {main.description}
                  </Text>
                </motion.article>
              )
            })()}

            {/* Right column - Side articles */}
            <motion.div className="grid gap-8 grid-rows-2" variants={staggerContainer}>
              {sideSections.map((section) => {
                const content = getSectionContent(section)

                return (
                  <motion.article
                    key={section.index}
                    className="overflow-hidden gap-4 grid md:grid-cols-2 group cursor-pointer"
                    onClick={() => handleArticleClick(section.index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => handleArticleKeyDown(e, section.index)}
                    aria-label={`Navigate to ${content.title}`}
                    variants={fadeUp}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="overflow-hidden flex-1">
                        <img
                          src={content.image}
                          alt={content.title}
                          className="w-full h-full aspect-video object-cover md:group-hover:scale-95 transition-transform duration-300"
                        />
                      </div>
                      {content.sectionTitle && (
                        <Text variant="body-sm" className="text-gray-600 hidden md:block">
                          {content.sectionTitle}
                        </Text>
                      )}
                    </div>

                    <div className="flex flex-col overflow-hidden gap-4">
                      <Heading level={4} className=" text-gray-100 md:group-hover:underline md:group-hover:decoration-wine-100 group-hover:underline-offset-4 transition-all duration-300">
                        {content.title}
                      </Heading>

                      <Text className="line-clamp-3" >
                        {content.description}
                      </Text>
                    </div>
                  </motion.article>
                )
              })}
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
  )
}

export default PressReleaseHighlights